import { DateRange, Goal, Task, Timestamp } from '@griply/types';
import { addDays, endOfDay, startOfDay } from 'date-fns';

import { isTaskMeasuredGoal } from './is-task-measured-goal';

const DEFAULT_TIMEFRAME_PADDING_DAYS = 30;
export type GetTimeframeTimesOptions = {
  allTasks: Task[];
  today: Timestamp;
};

export const getTimeframeTimes = (
  goal: Goal | undefined,
  { allTasks, today }: GetTimeframeTimesOptions,
): DateRange => {
  const usesTaskCompletions = isTaskMeasuredGoal(goal);

  const completedTaskTimes = usesTaskCompletions
    ? allTasks
        .filter(({ completedAt }) => !!completedAt)
        .map(({ completedAt }) => completedAt!.getTime())
    : [];

  const firstCompletionDate = completedTaskTimes.length
    ? new Date(Math.min(...completedTaskTimes))
    : undefined;
  const lastCompletionDate = usesTaskCompletions
    ? new Date(Math.max(...completedTaskTimes))
    : undefined;

  const metricEntryTimes =
    goal?.metric?.entries?.map(({ date }) => date.getTime()) ?? [];
  const firstMetricEntryDate = metricEntryTimes.length
    ? new Date(Math.min(...metricEntryTimes))
    : undefined;
  const lastMetricEntryDate = metricEntryTimes.length
    ? new Date(Math.max(...metricEntryTimes))
    : undefined;

  const startDate =
    firstCompletionDate ||
    firstMetricEntryDate ||
    goal?.startDate ||
    goal?.deadline
      ? startOfDay(
          new Date(
            Math.min(
              ...([
                firstCompletionDate?.getTime(),
                firstMetricEntryDate?.getTime(),
                goal?.deadline?.getTime(),
                goal?.startDate?.getTime(),
              ].filter(Boolean) as number[]),
            ),
          ),
        )
      : addDays(today, -DEFAULT_TIMEFRAME_PADDING_DAYS);

  const endTimestamps = [
    lastCompletionDate?.getTime(),
    lastMetricEntryDate?.getTime(),
    goal?.deadline?.getTime(),
    goal?.startDate
      ? addDays(goal.startDate, DEFAULT_TIMEFRAME_PADDING_DAYS).getTime()
      : undefined,
    !goal?.completedAt
      ? addDays(today, goal?.deadline ? 0 : DEFAULT_TIMEFRAME_PADDING_DAYS)
      : undefined,
  ].filter(Boolean) as number[];
  const endDate = endOfDay(
    new Date(
      endTimestamps.length ? Math.max(...endTimestamps) : today.getTime(),
    ),
  );

  return { startDate, endDate };
};
